import type { PropType } from "vue";
import { defineComponent, computed, inject, h, ref, nextTick } from "vue";
import { createSection } from "@formkit/inputs";
import { token } from "@formkit/utils";
import type { FormKitNode, FormKitFrameworkContext } from "@formkit/core";
import { parentSymbol, FormKitSchema } from "@formkit/vue";
import { showFormField } from "@/helpers/application-form/navigation";

const summary = createSection("summary", () => ({
	$el: "div",
	attrs: {
		"aria-live": "polite",
	},
}));

const summaryInner = createSection("summaryInner", () => ({
	$el: "div",
	if: "$summaries.length && $showSummaries",
}));

/**
 * @internal
 */
const messages = createSection("messages", () => ({
	$el: "ul",
	if: "$summaries.length && $showSummaries",
	attrs: {
		class: "list-disc list-outside",
	},
}));

/**
 * @internal
 */
const message = createSection("message", () => ({
	$el: "li",
	for: ["summary", "$summaries"],
	attrs: {
		key: "$summary.key",
		class: "text-sm text-warn hover:underline",
		"data-message-type": "$summary.type",
	},
}));

const messageLink = createSection("messageLink", () => ({
	$el: "a",
	attrs: {
		id: "$summary.key",
		href: '$: "#" + $summary.id',
		onClick: "$jumpLink",
	},
}));

/**
 * The actual schema to render for the messages.
 */
const definition = summary(
	summaryInner(messages(message(messageLink("$summary.message")))),
);

export interface FormKitSummaryMessage {
	message: string;
	id: string;
	key: string;
	type: string;
}

/**
 * Renders the messages for a parent node, or any node explicitly passed to it.
 * @public
 */
export const OurFormKitSummary = /* #__PURE__ */ defineComponent({
	props: {
		node: {
			type: Object as PropType<FormKitNode> | undefined,
			required: false,
		},
		forceShow: {
			type: Boolean,
			default: false,
		},
	},
	emits: {
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		show: (_summaries: FormKitSummaryMessage[]) => true,
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		summariesUpdated: (_summaries: FormKitSummaryMessage[]) => true,
	},
	setup(props, context) {
		const id = `summary-${token()}`;
		const node = computed<FormKitNode | undefined>(() => {
			return props.node || inject(parentSymbol, undefined);
		});

		if (!node.value)
			throw new Error(
				"FormKitSummary must have a FormKit parent or use the node prop.",
			);

		const summaryContexts = ref<FormKitFrameworkContext[]>([]);
		const showSummaries = ref(false);
		const summaries = computed((): FormKitSummaryMessage[] => {
			const summarizedMessages: FormKitSummaryMessage[] = [];
			summaryContexts.value.forEach((context) => {
				for (const idx in context.messages) {
					const message = context.messages[idx];
					if (!message || typeof message.value !== "string") continue;

					summarizedMessages.push({
						message: message.value,
						id: context.id,
						key: `${context.id}-${message.key}`,
						type: message.type,
					});
				}
			});

			if (showSummaries.value) {
				context.emit("summariesUpdated", summarizedMessages);
			}

			return summarizedMessages;
		});

		const addContexts = () => {
			summaryContexts.value = [];
			node.value?.walk(
				(child) => child.context && summaryContexts.value.push(child.context),
			);
		};

		node.value?.on("submit-raw", async () => {
			addContexts();
			if (summaries.value.length === 0) return;
			context.emit("show", summaries.value);
			showSummaries.value = true;
			await nextTick();
		});
		node.value?.on("child", addContexts);

		function jumpLink(e: MouseEvent) {
			if (e.target instanceof HTMLAnchorElement) {
				e.preventDefault();
				const id = e.target.getAttribute("href")?.substring(1);
				if (id) {
					showFormField(id, true);
				}
			}
		}

		const schema = definition({});

		const data = computed(() => {
			return {
				id,
				fns: node.value?.context?.fns || {},
				classes: node.value?.context?.classes || {},
				summaries: summaries.value,
				showSummaries: props.forceShow || showSummaries.value,
				summaryHeader: node.value?.context?.ui?.summaryHeader?.value || "",
				jumpLink,
			};
		});
		return () =>
			node.value?.context
				? h(FormKitSchema, { schema, data: data.value }, { ...context.slots })
				: null;
	},
});
